import React, { useState, DragEvent } from "react";
import { FiSave, FiPlus, FiX, FiLink } from "react-icons/fi";
import { LinkThreeData, LinkButton } from "../../types";
import LinksPreview from "./LinksPreview";
import utils from "../../helpers/utils";
import InputDefault from "../ui/InputDefault";
import { LinksIntroCard } from "./links/LinksIntroCard";
import { LinksColorsSection } from "./links/LinksColorsSection";
import { LinksButtonsList } from "./links/LinksButtonsList";

interface LinksManagementModalProps {
  isOpen: boolean;
  data: LinkThreeData;
  loading: boolean;
  onClose: () => void;
  onSave: (data: LinkThreeData) => Promise<boolean>;
  embedded?: boolean;
}

export interface LinksManagementModalHandle {
  save: () => Promise<boolean>;
}

export default React.forwardRef<
  LinksManagementModalHandle,
  LinksManagementModalProps
>(function LinksManagementModal(
  {
    isOpen,
    data,
    loading,
    onClose,
    onSave,
    embedded = false,
  }: LinksManagementModalProps,
  ref,
) {
  const [tempData, setTempData] = useState<LinkThreeData>(data);
  const [linkOpen, setLinkOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showIntro, setShowIntro] = useState(true);

  React.useEffect(() => {
    if (isOpen) {
      setTempData(data);
    }
  }, [isOpen, data]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const hidden = window.localStorage.getItem("easyfit_links_intro_hidden");
    if (hidden === "true") {
      setShowIntro(false);
    }
  }, []);

  const handleSave = async () => {
    return onSave(tempData);
  };

  React.useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  const addButton = () => {
    setTempData({
      ...tempData,
      buttons: [
        ...(tempData.buttons ?? []),
        {
          id: utils.generateId(),
          url: "",
          label: "Novo Link",
          clicks: 0,
          open: true,
          is_schedule: false,
          enabled: true,
        },
      ],
    });
  };

  const changeButton = (form: LinkButton) => {
    const btsn = tempData.buttons?.map((e) => {
      if (e.id === form.id) {
        return form;
      }
      return e;
    });
    setTempData({
      ...tempData,
      buttons: btsn,
    });
  };

  const removeButton = (id: string) => {
    const btsn = tempData.buttons.filter((e) => {
      return e.id !== id;
    });
    setTempData({
      ...tempData,
      buttons: btsn,
    });
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || !tempData?.buttons) return;

    const newButtons = [...tempData.buttons];
    const [draggedButton] = newButtons.splice(draggedIndex, 1);
    newButtons.splice(dropIndex, 0, draggedButton);

    setTempData({
      ...tempData,
      buttons: newButtons,
    });

    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (!isOpen && !embedded) return null;

  const containerClass = embedded
    ? "relative flex flex-col lg:flex-row w-full rounded-xl lg:rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark"
    : "relative flex flex-col lg:flex-row h-auto lg:h-[90vh] w-full max-w-7xl overflow-hidden rounded-xl lg:rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark";

  const contentClass = embedded
    ? "flex-1 flex-grid p-4 sm:p-6 lg:p-8"
    : "flex-1 flex-grid overflow-y-auto p-4 sm:p-6 lg:p-8";

  const content = (
    <div className={containerClass}>
      <LinksPreview data={tempData} />

      <div className="flex w-full flex-col lg:w-3/5">
        {!embedded && (
          <div className="flex items-center justify-between border-b border-stroke px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6 dark:border-strokedark">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <FiLink
                  className="text-purple-600 dark:text-purple-400"
                  size={18}
                />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-black dark:text-white">
                  Gerenciar Meu Site
                </h3>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                  {tempData.buttons?.length || 0}{" "}
                  {tempData.buttons?.length === 1 ? "link" : "links"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 sm:p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-meta-4 dark:hover:text-gray-300"
            >
              <FiX size={20} className="sm:w-6 sm:h-6" />
            </button>
          </div>
        )}
        <div className={contentClass}>
          <LinksIntroCard
            show={showIntro}
            onClose={() => {
              setShowIntro(false);
              if (typeof window !== "undefined") {
                window.localStorage.setItem(
                  "easyfit_links_intro_hidden",
                  "true",
                );
              }
            }}
          />
          <div className="mb-4 sm:mb-6 lg:mb-8 rounded-lg sm:rounded-xl border border-stroke bg-gray-50 p-3 sm:p-4 dark:border-strokedark dark:bg-meta-4/40">
            <div className="flex gap-2 sm:gap-3 flex-col flex-1">
              <div className="flex-1">
                <label className="mb-1.5 sm:mb-2 block text-sm sm:text-base font-semibold text-black dark:text-white">
                  Escolha um identificador único para seu site:
                </label>
                <InputDefault
                  id="minha_url"
                  name="minha_url"
                  label=""
                  value={tempData.minha_url || ""}
                  placeholder="ex: dra-joana-nutri"
                  onChange={(e) => {
                    const val = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, "");
                    setTempData({
                      ...tempData,
                      minha_url: val,
                    });
                  }}
                  className=""
                  classNameInput="text-sm"
                />
              </div>

              <div className="w-full flex-1 text-[10px] sm:text-[11px] leading-relaxed text-gray-600 dark:text-gray-300">
                Esse identificador deve ser único. Use apenas letras, números e
                hífens. <br />
                <span className="font-semibold italic">
                  Exemplo: {window.location.origin}/s/seu-endereco
                </span>
              </div>
            </div>
          </div>

          <LinksColorsSection
            open={linkOpen}
            onToggle={() => setLinkOpen(!linkOpen)}
            data={tempData}
            onChange={setTempData}
          />

          <button
            onClick={addButton}
            className="mb-4 sm:mb-6 lg:mb-8 inline-flex w-full items-center justify-center gap-2 sm:gap-3 rounded-lg sm:rounded-xl border-2 border-dashed border-primary bg-purple-50/50 px-4 py-3 sm:px-6 sm:py-5 text-sm sm:text-base font-semibold text-primary transition-all hover:border-purple-600 hover:bg-purple-600 hover:text-white hover:shadow-md active:scale-95 dark:bg-purple-900/20 dark:hover:bg-purple-600"
          >
            <FiPlus size={18} className="sm:w-5.5 sm:h-5.5" />
            Adicionar Novo Link
          </button>

          <LinksButtonsList
            buttons={tempData.buttons || []}
            draggedIndex={draggedIndex}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onChangeButton={changeButton}
            onRemoveButton={removeButton}
          />
        </div>

        {!embedded && (
          <div className="border-t border-stroke bg-gray-50 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 dark:border-strokedark dark:bg-meta-4">
            <div className="flex flex-col gap-2.5 sm:flex-row sm:justify-end sm:gap-3">
              <button
                onClick={onClose}
                className="order-2 sm:order-1 rounded-lg border border-stroke bg-white px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-black transition-all hover:bg-gray-50 hover:shadow-md active:scale-95 dark:border-strokedark dark:bg-boxdark dark:text-white dark:hover:bg-meta-4"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="order-1 sm:order-2 inline-flex items-center justify-center gap-2 sm:gap-2.5 rounded-lg bg-purple-600 px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md active:scale-95 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <FiSave size={16} className="sm:w-4.5 sm:h-4.5" />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="fixed inset-0 z-[999999] flex items-start sm:items-center justify-center bg-black/50 p-2 sm:p-4 backdrop-blur-sm overflow-y-auto">
      {content}
    </div>
  );
});
